import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  Avatar,
  Rating,
  Chip
} from '@mui/material';
import { 
  ThumbUp,
  Favorite,
  CheckCircle
} from '@mui/icons-material';

interface AnimatedNumberProps {
  value: string;
  duration?: number;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  // Extraer el número y el sufijo (+, %, etc.)
  const match = value.match(/^(\d+)(.*)$/);
  const targetNumber = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : '';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const startValue = 0;
    const endValue = targetNumber;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Función de easing para una animación suave y uniforme
      // Usando easeOutCubic para una animación más uniforme sin detenerse al final
      const easeOutCubic = 1 - Math.pow(1 - progress, 2);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutCubic);

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, targetNumber, duration]);

  return (
    <span ref={elementRef}>
      {count}{suffix}
    </span>
  );
};

const Testimonials: React.FC = () => {
  const testimonials = [
    {
      name: 'María González',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      title: 'Excelente atención y resultados',
      text: 'Estoy muy contenta con el tratamiento que recibí en KMO Smile. El personal es muy amable y los resultados superaron mis expectativas. Mi sonrisa nunca se había visto tan bien.',
      rating: 5,
      treatment: 'Ortodoncia',
      date: 'Enero 2024'
    },
    {
      name: 'Carlos Rodríguez',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      title: 'Profesionales muy capacitados',
      text: 'El equipo de KMO Smile es muy profesional y me brindó un servicio de alta calidad. Estoy muy satisfecho con los resultados de mi tratamiento de implantes.',
      rating: 5,
      treatment: 'Implantes Dentales',
      date: 'Diciembre 2023'
    },
    {
      name: 'Ana Martínez',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      title: 'Recomiendo ampliamente',
      text: 'Recomiendo ampliamente KMO Smile. El ambiente es muy agradable y los profesionales son muy atentos y competentes. Mi blanqueamiento dental quedó perfecto.',
      rating: 5,
      treatment: 'Blanqueamiento Dental',
      date: 'Noviembre 2023'
    },
    {
      name: 'Roberto Silva',
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=150&h=150&fit=crop&crop=face',
      title: 'Tratamiento sin dolor',
      text: 'Tenía mucho miedo de ir al dentista, pero en KMO Smile me hicieron sentir muy cómodo. El tratamiento de conducto fue completamente indoloro.',
      rating: 5,
      treatment: 'Endodoncia',
      date: 'Octubre 2023'
    },
    {
      name: 'Patricia Ruiz',
      image: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=150&h=150&fit=crop&crop=face',
      title: 'Atención personalizada',
      text: 'Me encantó la atención personalizada que recibí. El doctor se tomó el tiempo de explicarme todo el proceso y los resultados son increíbles.',
      rating: 5,
      treatment: 'Carillas de Porcelana',
      date: 'Septiembre 2023'
    },
    {
      name: 'Javier Fernández',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      title: 'Equipo excepcional',
      text: 'El equipo de KMO Smile es excepcional. Desde la recepción hasta el doctor, todos son muy profesionales y amables. Mi experiencia fue excelente.',
      rating: 5,
      treatment: 'Limpieza Profunda',
      date: 'Agosto 2023'
    }
  ];

  const stats = [
    { number: '500+', label: 'Pacientes Satisfechos' },
    { number: '98%', label: 'Tasa de Satisfacción' },
    { number: '15+', label: 'Años de Experiencia' },
    { number: '100%', label: 'Garantía de Calidad' }
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: '#946f56',
          color: 'white',
          py: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Testimonios de nuestros pacientes
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Descubre por qué nuestros pacientes confían en KMO Smile
          </Typography>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', mb: 6 }}>
          {stats.map((stat, index) => (
            <Box key={index} sx={{ textAlign: 'center', flex: '1 1 200px' }}>
              <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', color: '#D2691E', mb: 1 }}>
                {/* duration: más bajo = más rápido (1000 = 1s), más alto = más lento (5000 = 5s, 6000 = 6s) */}
                <AnimatedNumber value={stat.number} duration={6000} />
              </Typography>
              <Typography variant="h6" component="h3" sx={{ color: '#2C3E50' }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Testimonials Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography 
          variant="h4" 
          component="h2" 
          sx={{ 
            fontWeight: 'bold',
            color: '#2C3E50',
            mb: 5,
            textAlign: 'center'
          }}
        >
          Experiencias Reales
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
          {testimonials.map((testimonial, index) => (
            <Card 
              key={index} 
              sx={{ 
                maxWidth: 400,
                flex: '1 1 350px',
                backgroundColor: '#FFE4B5',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    src={testimonial.image}
                    alt={testimonial.name}
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      mr: 2,
                      border: '2px solid white',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                      {testimonial.name}
                    </Typography>
                    <Chip 
                      label={testimonial.treatment} 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#D2691E', 
                        color: 'white',
                        fontSize: '0.75rem'
                      }} 
                    />
                  </Box>
                </Box>
                
                <Typography 
                  variant="h6" 
                  component="h4" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#2C3E50',
                    mb: 2
                  }}
                >
                  {testimonial.title}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ 
                    lineHeight: 1.6,
                    mb: 3
                  }}
                >
                  {testimonial.text}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Rating 
                    value={testimonial.rating} 
                    readOnly 
                    sx={{ color: '#D2691E' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {testimonial.date}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      {/* Why Trust Us Section */}
      <Box sx={{ backgroundColor: '#F8F9FA', py: 6 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              fontWeight: 'bold',
              color: '#2C3E50',
              mb: 5,
              textAlign: 'center'
            }}
          >
            ¿Por qué confiar en KMO Smile?
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center', flex: '1 1 250px' }}>
              <CheckCircle sx={{ fontSize: 50, color: '#D2691E', mb: 2 }} />
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                Profesionales Certificados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Nuestro equipo cuenta con certificaciones internacionales y formación continua
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', flex: '1 1 250px' }}>
              <ThumbUp sx={{ fontSize: 50, color: '#D2691E', mb: 2 }} />
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                Tecnología de Vanguardia
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Equipamiento de última generación para tratamientos precisos y efectivos
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', flex: '1 1 250px' }}>
              <Favorite sx={{ fontSize: 50, color: '#D2691E', mb: 2 }} />
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                Atención Personalizada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cada paciente recibe un tratamiento único adaptado a sus necesidades
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          ¿Listo para ser nuestro próximo paciente satisfecho?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Únete a los cientos de pacientes que ya han transformado su sonrisa con nosotros
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h5" component="span" sx={{ color: '#D2691E', fontWeight: 'bold' }}>
            ⭐ 4.9/5
          </Typography>
          <Typography variant="h5" component="span" sx={{ color: '#2C3E50' }}>
            basado en {testimonials.length} testimonios
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonials; 